import * as React from 'react';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';
import { ThemeProvider, createTheme, styled } from '@mui/material/styles';
import { useState, useEffect } from 'react';
import Label from '../../Label';
import useAuth from '../../../hooks/useAuth';
import { ticketApi } from '../../../api/ticketApi';
import toast from 'react-hot-toast';
import { Grid, Backdrop, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, Divider, IconButton, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ClientComplaintForm from './incidents/ClientComplaintForm';
import ShowTicketManager from './incidents/show_ticket_manager';
import ShowTicketReferent from './incidents/show_ticket_referent';
import { motion, AnimatePresence } from 'framer-motion';
import Scrollbar from '../../Scrollbar';

import TicketDialogManager from './incidents/TicketDialogManager';
import TicketDialogReferent from './incidents/TicketDialogReferent';
import { ClickAwayListener } from '@mui/base';

import moment from 'moment';

const theme = createTheme({
  palette: {
    light: {
      main: '#04ba37',
      contrastText: '#fff'
    },
    moderate: {
      main: '#fabd05',
      contrastText: '#fff'
    },
    severe: {
      main: '#ff7700',
      contrastText: '#fff'
    },
    critical: {
      main: '#ba0404',
      contrastText: '#fff'
    },
    red: {
      main: 'red',
      contrastText: '#fff'
    },
    grey: {
      main: 'grey',
      contrastText: '#fff'
    },
    orange: {
      main: 'orange',
      contrastText: '#fff'
    },
    green: {
      main: 'green',
      contrastText: '#fff'
    },
    suspendu: {
      main: '#f44336',
      contrastText: '#fff'
    }
  },
});
const LimitedBackdrop = styled(Backdrop)({
  zIndex: 1
});

const capitalizeFirstLetter = (string) => string?.charAt(0).toUpperCase() + string?.slice(1);

const severities = [
  {color: 'light', value: 'Mineure'},
  {color: 'moderate', value: 'Modérée'},
  {color: 'severe', value: 'Grave'},
  {color: 'critical', value: 'Critique'},
];

//Joseph_com Formatter de date français
const formatDate = (date) => (date ? new Date(date) : null);

//Joseph_com Formatter d'affichage pour DataGrid
const formatDateDisplay = (date) => (date ? new Date(date).toLocaleString('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
}) : '-');

// Libellé court d'une session pour l'onglet "Réclamation Client"
const formatSession = (s) => {
  if (!s) return '—';
  const num = s.number_session ? `Session ${s.number_session}` : 'Session';
  const start = s.start_date ? new Date(s.start_date).toLocaleDateString('fr-FR') : '';
  return start ? `${num} · ${start}` : num;
};

//Joseph_com Colonne gravité
const severityColumn = () => ({
  field: 'severity',
  headerName: 'Gravité',
  flex: 0.8,
  renderCell: (ticket) => {
    const severityIndex = ticket.row.severity;
    const severity = severities[severityIndex] || { color: 'grey', value: 'Inconnue' };
    return (
      <ThemeProvider theme={theme}>
        <Label color={severity.color}>{severity.value}</Label>
      </ThemeProvider>
    );
  }
});

//Joseph_com Colonne état
const stateColumn = () => ({
  field: 'state',
  headerName: 'Etat',
  flex: 0.8,
  renderCell: (params) => (
    <ThemeProvider theme={theme}>
      <Label color={
        params.row.state === 'Ouvert' ? 'grey' :
        params.row.state === 'Suspendu' ? 'suspendu' :
        params.row.state === 'En cours' ? 'orange' :
        'green'
      }>
        {capitalizeFirstLetter(params.row.state)}
      </Label>
    </ThemeProvider>
  )
});

//Joseph_com Colonne cible
const targetColumn = () => ({
  field: 'target',
  headerName: 'Cible',
  type: 'string',
  flex: 1,
  valueGetter: (params) => {
    if (!params.row.customer) return 'Interne';
    return capitalizeFirstLetter(`${params.row.customer.firstname} ${params.row.customer.lastname}`);
  }
});

//Joseph_com Colonne référent
const referentColumn = () => ({
  field: 'referent',
  headerName: 'Référent',
  type: 'string',
  flex: 1,
  valueGetter: (params) => {
    if (!params.row.user_in_charge) return 'Aucun';
    return `${params.row.user_in_charge.firstname} ${params.row.user_in_charge.lastname}`;
  }
});

//Joseph_com Colonne délai théorique
const normalDelayColumn = () => ({
  field: 'normal_delay',
  headerName: 'Délai théorique',
  type: 'number',
  flex: 1,
  valueGetter: (params) => `${params.row.normal_delay} jour${params.row.normal_delay >= 2 ? 's' : ''}`
});

//Joseph_com Colonne date générique
const dateColumn = (field, headerName, flex = 1) => ({
  field,
  headerName,
  type: 'dateTime',
  flex,
  valueGetter: (params) => formatDate(params.row[field]),
  valueFormatter: (params) => formatDateDisplay(params.value)
});


//Joseph_com Colonne des tickets incidents
export const columnsScheme = [
  { field: 'reference', headerName: 'Référence', flex: 1 },
  { field: 'category', headerName: "Catégorie de l'incident", flex: 1 },

  severityColumn(),
  stateColumn(),
  targetColumn(),
  referentColumn(),
  normalDelayColumn(),

  dateColumn('created_at', 'Date de création'),
  dateColumn('take_charge_date', 'Date de prise en charge', 1.2),
  dateColumn('approx_resolution_date', 'Date approx. de résolution'),
  dateColumn('effective_resolution_date', 'Date effective de résolution')
];

//Joseph_com Colonne des réclamations
export const columnsSchemeComplaint = [
  { field: 'reference', headerName: 'Référence', flex: 1 },
  { field: 'category', headerName: 'Catégorie de la réclamation', flex: 1 },

  severityColumn(),
  stateColumn(),
  targetColumn(),
  referentColumn(),
  normalDelayColumn(),

  dateColumn('created_at', 'Date de création'),
  dateColumn('take_charge_date', 'Date de prise en charge', 1.2),
  dateColumn('approx_resolution_date', 'Date approx. de résolution'),
  dateColumn('effective_resolution_date', 'Date effective de résolution')
];

//Joseph_com Colonne du service
export const serviceObject = {
  field: 'service',
  headerName: 'Service',
  flex: 1,
  renderCell: (params) => <Box>{params.row.service}</Box>
};


export default function TicketList(props) {
  const { tickets, customerPage, isLoading, update, setUpdate, getTickets, type, currentTab } = props;
  const { user } = useAuth();
  // Onglet "Réclamation Client" : jeu de colonnes dédié (apprenant / formation / session / affectation / réclamation)
  const isClientComplaint = type === 'complaint' && currentTab === 'Réclamation Client';
  const headCellSx = { borderBottom: 'none', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem' };
  // Modal détail (lecture seule) pour une réclamation client, alimenté par la ligne cliquée
  const [clientDetailOpen, setClientDetailOpen] = useState(false);
  const [clientDetail, setClientDetail] = useState(null);
  // Modal d'édition d'une réclamation client
  const [clientEditOpen, setClientEditOpen] = useState(false);

  // Bascule "Terminé" (Résolu) / "Ouvert" d'une réclamation depuis la liste
  const toggleComplaintDone = async (ticket) => {
    const newDone = ticket.state !== 'Résolu';
    try {
      await ticketApi.setComplaintDone(localStorage.getItem('accessToken'), ticket.id, newDone, user.id);
      toast.success(newDone ? 'Réclamation marquée comme terminée' : 'Réclamation rouverte');
      getTickets();
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };
  const [columns, setColumns] = useState(columnsScheme);
  const [selectionModel, setSelectionModel] = useState([]);
  const [ticketComment, setTicketComment] = useState(null);
  const [show, setShow] = useState(false);
  const [commentList, setCommentList] = useState([]);
  const [infoTicket, setInfoTicket] = useState([]);
  const [ticketService, setTicketService] = useState(null);
  const [ticketCategory, setTicketCategory] = useState(null);
  const [ticketSeverity, setTicketSeverity] = useState(null);
  const [ticketReferentId, setTicketReferentId] = useState(null);
  const [ticketReferentName, setTicketReferentName] = useState(null);
  const [activateFolliwTicketStatus, setActivateFolliwTicketStatus] = useState(true);
  const [ticketSelectedId, setTicketSelectedId] = useState(null);
  const [ticketLog, setTicketLog] = useState(null);

  const handleShow = async () => { setShow(!show); };

  const [ticketSolution, setTicketSolution] = useState(null);
  const [aiNotSolvedButtonColor, setAiNotSolvedButtonColor] = useState('black');
  const [ticketState, setTicketState] = useState('suspendu');
  const [listServices, setListServices] = useState([]);
  const [listCategories, setListCategories] = useState([]);
  const [listMembers, setListMembers] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [ticketDescription, setTicketDescription] = useState(null);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
   // to compare values in the log when modifying the ticket info by the a manager /////////////////
   const [oldTicketService, setOldTicketService] = useState(null);
   const [oldTicketSeverity, setOldTicketSeverity] = useState(null);
   const [oldTicketReferentId, setOldTicketReferentId] = useState(null);
   const [oldTicketTracking, setOldTicketTracking] = useState(null);
   const [oldTicketSolution, setOldTicketSolution] = useState(null);
   const [oldTicketState, setOldTicketState] = useState(null);
   const [oldTicketMajorIncident, setOldTicketMajorIncident] = useState(null);
   const [oldTicketCategory, setOldTicketCategory] = useState(null);
   const [oldTicketDescription, setOldTicketDescription] = useState(null);
   const [oldTicketAcceptTracking, setOldTicketAcceptTracking] = useState(null);
   const [oldTicketDRA, setOldTicketDRA] = useState(null); // date res approx
   const [oldTicketDREff, setOldTicketDREff] = useState(null); // date res eff
   const [oldTicketDStart, setOldTicketDStart] = useState(null); // date prise en charge
   const [oldTicketCause, setOldTicketCause] = useState(null); // date res eff
   const [oldTicketCorrectiveActions, setOldTicketCorrectiveActions] = useState([]);
 // New values //////////////////
 const [newTicketService, setNewTicketService] = useState(null);
 const [newTicketSeverity, setNewTicketSeverity] = useState(null);
 const [newTicketReferentId, setNewTicketReferentId] = useState(null);
 const [newTicketTracking, setNewTicketTracking] = useState(null);
 const [newTicketSolution, setNewTicketSolution] = useState(null);
 const [newTicketState, setNewTicketState] = useState(null);
 const [newTicketMajorIncident, setNewTicketMajorIncident] = useState(null);
 const [newTicketCategory, setNewTicketCategory] = useState(null);
 const [newTicketDescription, setNewTicketDescription] = useState(null);
 const [newTicketAcceptTracking, setNewTicketAcceptTracking] = useState(null);
 const [newTicketDRA, setNewTicketDRA] = useState(null); // date res approx
 const [newTicketDREff, setNewTicketDREff] = useState(null); // date res eff
 const [newTicketDStart, setNewTicketDStart] = useState(null); // date prise en charge
 const [newTicketCause, setNewTicketCause] = useState(null); // date res eff
 const [ newTicketCorrectiveActions, setNewTicketCorrectiveActions] = useState([]);
 const [ticketFiles, setTicketFiles] = useState(null);
 const [majorIncident, setMajorIncident] = useState(null);
 const [loading, setLoading] = useState(false); 
   
  const getCategories = async (serviceId) => {
    try {
      const categorieList = await ticketApi.getCategoriesMemberTicket(localStorage.getItem('accessToken'), serviceId, type);
      setListCategories(categorieList);
    } catch (err) {
      console.log(err);
    }
  };
  const getServiceMembers = async (serviceId) => {
    try {
      const memberList = await ticketApi.getServiceMembersMemberTicket(localStorage.getItem('accessToken'), serviceId);
      setListMembers(memberList);
    } catch (err) {
      console.log(err);
    }
  };  
  const getTicketComments = async (ticketId) => {
    try {
      const listComments = await ticketApi.getTicketComments(localStorage.getItem('accessToken'), ticketId);
      setCommentList(listComments);
    } catch (err) {
      console.log(err);
    }
  }; 
  
  const getServices = async () => {
    try {
      const serviceList = await ticketApi.getServicesMemberTicket(localStorage.getItem('accessToken'));
      setListServices(serviceList);
    } catch (err) {
      console.log(err);
    }
  };
  const handleClick = async (selectedRowID) => {
    setTicketSelectedId(selectedRowID);
    await getTicketComments(selectedRowID);
    handleShow();
  };
  useEffect(() => {
    if (customerPage && type === 'ticket') {
      const newColumns = [...columnsScheme];
      newColumns[4] = serviceObject;
      setColumns(newColumns);
    }    

    else  if (type !== 'ticket') {setColumns(columnsSchemeComplaint);}
  }, [customerPage, type]);
  useEffect(() => {
    getServices();
  }, []);
 
  const validateTicket = async (ticketId) => {
    try {
      await ticketApi.validateTicket(localStorage.getItem('accessToken'), ticketId, user.id, oldTicketAcceptTracking, newTicketAcceptTracking, newTicketDescription);
      toast.success('Ticket validé avec succès!');
      getTickets(type);
    } catch (err) {toast.error("Erreur lors de la validation du ticket!");
      console.log(err);
    }
  };
  const openTicket = async (ticketId) => {
    try {
      await ticketApi.openTicket(localStorage.getItem('accessToken'), ticketId, user.id, newTicketAcceptTracking, newTicketDescription, infoTicket.ref, infoTicket.learnerId, infoTicket.date_creation, infoTicket.creator_id, newTicketState);
      
      if (type === 'ticket') {
        toast.success('Ticket ouvert avec succès!');
      } else {toast.success('réclamation ouvert avec succès!'); }
      getTickets(type);
    } catch (err) {toast.error("Erreur lors de l'ouverture du ticket!");
      console.log(err);
    }
  };
  const deleteTicket = async (ticketId) => {
    try {
      await ticketApi.deleteTicket(localStorage.getItem('accessToken'), ticketId, user.id);
      if (type === 'ticket') {
        toast.success('Ticket supprimé avec succès!');
      } else {toast.success('réclamation supprimé avec succès!'); }
      // getTickets(type);
      // toast.success('Ticket supprimé avec succès!');
      getTickets(type);
    } catch (err) {toast.error("Erreur lors de la suppression du ticket!");
      console.log(err);
    }
  };
 const updateTicketInfo = async (userId, ticketId) => {
    try {
      await ticketApi.updateTicketInfo(
        localStorage.getItem('accessToken'),
        ticketId,
        
        newTicketService,
        newTicketSeverity,
        newTicketReferentId,
        newTicketTracking,
        newTicketSolution,
        newTicketState,
        newTicketMajorIncident,
        newTicketCategory,
        newTicketAcceptTracking,
        newTicketDescription,
        newTicketDStart,
        newTicketDRA,
        newTicketDREff,
        newTicketCause,
        
        oldTicketService, 
        oldTicketSeverity, 
        oldTicketReferentId, 
        oldTicketTracking, 
        oldTicketSolution, 
        oldTicketState, 
        oldTicketMajorIncident, 
        oldTicketCategory,
        oldTicketAcceptTracking, 
        oldTicketDescription,
        oldTicketDStart,
        oldTicketDRA, 
        oldTicketDREff,
        oldTicketCause,

        userId,
        infoTicket.ref, 
        infoTicket.learnerId,);
      // getTickets(type);
      // toast.success('Ticket modifié avec succès!');
      if (type === 'ticket') {
        toast.success('Ticket modifié avec succès!');
      } else {toast.success('réclamation modifié avec succès!'); }
      getTickets(type);
    } catch (err) {toast.error("Erreur lors de la modification du ticket!");
      console.log(err);
    }
  };
 
const getTicketLog = async (ticketId) => {
    try {
      const log = await ticketApi.getTicketLog(localStorage.getItem('accessToken'), ticketId);
      setTicketLog(log);
    } catch (err) { 
      console.log(err);
    }
  };

  const addNewComment = async (Comment, userId, ticketId) => {
    try {
      await ticketApi.addNewComment(localStorage.getItem('accessToken'), Comment, ticketId, userId);
      getTicketComments(ticketId);
      getTicketLog(ticketId);
      toast.success('commentaire ajouté avec succès!');
    } catch (err) {toast.error("erreur lors de l'ajout du commentaire!");
      console.log(err);
    }
  }; 

  const getTicketDocuments = async (ticketId) => {
    try {
      const Doc = await ticketApi.getTicketDocuments(localStorage.getItem('accessToken'), ticketId);
      setTicketFiles(Doc);
      
    } catch (err) { 
      console.log(err);
    }
  };
  const deleteTicketDocument = async (docId, ticketId) => {
    try {
      const Doc = await ticketApi.deleteTicketDocument(localStorage.getItem('accessToken'), docId, ticketId);
      setTicketFiles(Doc);
    } catch (err) { toast.error("erreur lors de la suppression du document!");
      console.log(err);
    }
  };
  const getTicketCorrectiveActions = async (ticketId) => {
    if (!ticketId) return;
    const correctiveActions = await ticketApi.fetchCorrectiveActionsByTicketId(localStorage.getItem('accessToken'), ticketId)
console.log(correctiveActions);
    setOldTicketCorrectiveActions(correctiveActions)
  }
  const getTicketInfo = async (ticketId) => {
   setLoading(true); 
   try {
      const ticketInfoG = await ticketApi.getTicketInfo(localStorage.getItem('accessToken'), ticketId);
      const ticketInfo = ticketInfoG.info;
      
      setInfoTicket(ticketInfo[0]);
      
      if (ticketInfoG.incident_id) {setMajorIncident(ticketInfoG.incident_id[0]);}
      
      getTicketDocuments(ticketId);
      
      getTicketLog(ticketId);
      
      setSelectedService(ticketInfo[0]?.service); 
      
      getCategories(ticketInfo[0]?.service);
      getTicketCorrectiveActions(ticketId);
      if (ticketInfo[0]?.service === 1) {
         getServiceMembers(0);
      } else {
         getServiceMembers(ticketInfo[0].service);
      }
      
      // to compare values in the log when modifying the ticket info by the a manager ///////////////////////
      setOldTicketService(ticketInfo[0].service);
      setOldTicketSeverity(ticketInfo[0].severity);
      setOldTicketReferentId(ticketInfo[0].referent_id);
      setOldTicketTracking(ticketInfo[0].tracking);
      setOldTicketSolution(ticketInfo[0].solution);
      setOldTicketState(ticketInfo[0].state);
      setOldTicketMajorIncident(ticketInfo[0].majorIncident_id);
      setOldTicketCategory(ticketInfo[0].category_id);
      setOldTicketDescription(ticketInfo[0].description);
      setOldTicketCause(ticketInfo[0].causes);
      setOldTicketDStart(ticketInfo[0].date_take_charge);
      setOldTicketDRA(ticketInfo[0].date_res_approx);
      setOldTicketDREff(ticketInfo[0].date_res_eff);
      // for modifying ////////////////////////////////
      setNewTicketService(ticketInfo[0].service);
      setNewTicketSeverity(ticketInfo[0].severity);
      setNewTicketReferentId(ticketInfo[0].referent_id);
      setNewTicketTracking(ticketInfo[0].tracking);
      setNewTicketSolution(ticketInfo[0].solution);
      setNewTicketState(ticketInfo[0].state);
      setNewTicketMajorIncident(ticketInfo[0].majorIncident_id);
      setNewTicketCategory(ticketInfo[0].category_id);
      setNewTicketDescription(ticketInfo[0].description);
      setNewTicketCause(ticketInfo[0].causes);
      setNewTicketDStart(ticketInfo[0].date_take_charge)
      setNewTicketDRA(ticketInfo[0].date_res_approx);
      setNewTicketDREff(ticketInfo[0].date_res_eff);
      if (ticketInfo[0].tracking === 1) {
        setOldTicketAcceptTracking(true);
     } else {
      setOldTicketAcceptTracking(false);
     }
    
      if (ticketInfo[0].tracking === 1) {
         setNewTicketAcceptTracking(true);
      } else {
        setNewTicketAcceptTracking(false);
      }
     
      // setOldTicketTCD(ticketInfo[0].date_take_charge);
      setOldTicketDRA(ticketInfo[0].date_res_approx);
      setNewTicketDRA(ticketInfo[0].date_res_approx);
      // setOldTicketDRE(ticketInfo[0].date_res_eff);
      setLoading(false); 
    } catch (err) { 
      console.log(err);
      setLoading(false); 
    }
  };

  const handleClose = () => {
    setTicketService(null);
    setTicketSeverity(null);
    setTicketReferentId(null);
    setTicketReferentName(null);
    setTicketDescription(null);
    setActivateFolliwTicketStatus(false);
    setTicketSolution(null);
    setTicketComment(null);
    setTicketState('Ouvert');
    setAiNotSolvedButtonColor('black');
  };
  const handleKeyUp = (e) => {
    if (e.code === 'Enter' && !e.shiftKey && ticketComment) {
      addNewComment(ticketComment, user.id, ticketSelectedId);
      
      setTicketComment('');
    
     } };
    const handleTicketCommentChange = (event) => {
      setTicketComment(event.target.value);
    };
   
  const paginatedTickets = tickets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const getSeverityLabel = (severityIndex) => {
    const severity = severities[severityIndex] || { color: 'grey', value: 'Inconnue' };
    return <Label color={severity.color}>{severity.value}</Label>;
  };

  const getStateLabel = (state) => {
    return <Label color={
      state === 'Ouvert' ? 'grey' :
      state === 'Suspendu' ? 'suspendu' :
      state === 'En cours' ? 'orange' :
      'green'
    }>{capitalizeFirstLetter(state)}</Label>;
  };

  // Champ libellé/valeur pour le modal détail de réclamation client
  const renderDetailField = (label, value) => (
    <Box sx={{ mb: 0.5 }}>
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#1e293b', mt: 0.3, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {value || '—'}
      </Typography>
    </Box>
  );

  return (
    <Box>
       <LimitedBackdrop
                  sx={{ color: '#fff', zIndex: () => theme.zIndex.drawer + 1 , height: '100%'}}
                  open={loading}
                >
                  <CircularProgress 
                  color="primary"
                   />
      </LimitedBackdrop>
      
      <Box sx={{ width: '100%' }}>
        <Scrollbar>
          <Box sx={{ minWidth: 1200, px: 2 }}>
            <Table sx={{ borderCollapse: "separate", borderSpacing: "0 8px" }}>
              <TableHead>
                {isClientComplaint ? (
                  <TableRow>
                    <TableCell sx={headCellSx}>Référence</TableCell>
                    <TableCell sx={headCellSx}>Apprenant</TableCell>
                    <TableCell sx={headCellSx}>Formation</TableCell>
                    <TableCell sx={headCellSx}>Session</TableCell>
                    <TableCell sx={headCellSx}>Affectation</TableCell>
                    <TableCell sx={headCellSx}>Réclamation</TableCell>
                    <TableCell sx={headCellSx} align="center">Terminé</TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell sx={{ borderBottom: "none", fontWeight: 800, color: "#64748b", textTransform: "uppercase", fontSize: "0.75rem" }}>Référence</TableCell>
                    <TableCell sx={{ borderBottom: "none", fontWeight: 800, color: "#64748b", textTransform: "uppercase", fontSize: "0.75rem" }}>Catégorie</TableCell>
                    <TableCell sx={{ borderBottom: "none", fontWeight: 800, color: "#64748b", textTransform: "uppercase", fontSize: "0.75rem" }}>Gravité</TableCell>
                    <TableCell sx={{ borderBottom: "none", fontWeight: 800, color: "#64748b", textTransform: "uppercase", fontSize: "0.75rem" }}>Etat</TableCell>
                    {customerPage && type === 'ticket' && (
                      <TableCell sx={{ borderBottom: "none", fontWeight: 800, color: "#64748b", textTransform: "uppercase", fontSize: "0.75rem" }}>Service</TableCell>
                    )}
                    <TableCell sx={{ borderBottom: "none", fontWeight: 800, color: "#64748b", textTransform: "uppercase", fontSize: "0.75rem" }}>Cible</TableCell>
                    <TableCell sx={{ borderBottom: "none", fontWeight: 800, color: "#64748b", textTransform: "uppercase", fontSize: "0.75rem" }}>Référent</TableCell>
                    <TableCell sx={{ borderBottom: "none", fontWeight: 800, color: "#64748b", textTransform: "uppercase", fontSize: "0.75rem" }}>Délai théorique</TableCell>
                    <TableCell sx={{ borderBottom: "none", fontWeight: 800, color: "#64748b", textTransform: "uppercase", fontSize: "0.75rem" }}>Création</TableCell>
                  </TableRow>
                )}
              </TableHead>
              <TableBody>
                <AnimatePresence>
                  {paginatedTickets.map((ticket, index) => {
                    const isSelected = ticketSelectedId === ticket.id;

                    return (
                      <TableRow 
                        component={motion.tr}
                        key={ticket.id} 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, delay: index * 0.02 }}
                        onClick={() => {
                          if (isClientComplaint) {
                            setClientDetail(ticket);
                            setClientDetailOpen(true);
                          } else {
                            setTicketSelectedId(ticket.id);
                            getTicketInfo(ticket.id);
                            handleClick(ticket.id);
                          }
                        }}
                        sx={{ 
                          backgroundColor: isSelected ? "#f8fafc" : "white", 
                          boxShadow: isSelected ? "inset 4px 0 0 #3b82f6, 0 1px 3px rgba(0,0,0,0.05)" : "0 1px 3px rgba(0,0,0,0.05)",
                          transition: "transform 0.2s, box-shadow 0.2s, background-color 0.2s",
                          cursor: "pointer",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                            backgroundColor: "#ffffff",
                          }
                        }}
                      >
                        {isClientComplaint ? (
                          <>
                            <TableCell sx={{ borderBottom: "none", borderRadius: "12px 0 0 12px" }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                                {ticket.reference}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ borderBottom: "none", color: "#475569", fontWeight: 600 }}>
                              {ticket.customer ? capitalizeFirstLetter(`${ticket.customer.firstname} ${ticket.customer.lastname}`) : '—'}
                            </TableCell>
                            <TableCell sx={{ borderBottom: "none", color: "#475569", fontWeight: 600 }}>
                              {ticket.formation || '—'}
                            </TableCell>
                            <TableCell sx={{ borderBottom: "none", color: "#475569", fontWeight: 600 }}>
                              {formatSession(ticket.session)}
                            </TableCell>
                            <TableCell sx={{ borderBottom: "none", color: "#475569", fontWeight: 600 }}>
                              {ticket.user_in_charge ? `${ticket.user_in_charge.firstname} ${ticket.user_in_charge.lastname}` : 'Non affecté'}
                            </TableCell>
                            <TableCell sx={{ borderBottom: "none", color: "#475569", maxWidth: 320 }}>
                              <Typography
                                variant="body2"
                                title={ticket.description || ''}
                                sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 320 }}
                              >
                                {ticket.description || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ borderBottom: "none", borderRadius: "0 12px 12px 0" }} onClick={(e) => e.stopPropagation()}>
                              <Tooltip title={ticket.state === 'Résolu' ? 'Terminée — cliquer pour rouvrir' : 'Marquer comme terminée'}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); toggleComplaintDone(ticket); }}
                                  sx={{ color: ticket.state === 'Résolu' ? '#16a34a' : '#cbd5e1' }}
                                >
                                  {ticket.state === 'Résolu' ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell sx={{ borderBottom: "none", borderRadius: "12px 0 0 12px" }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                                {ticket.reference}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ borderBottom: "none", color: "#475569", fontWeight: 600 }}>
                              {ticket.category}
                            </TableCell>
                            <TableCell sx={{ borderBottom: "none" }}>
                              <ThemeProvider theme={theme}>
                                {getSeverityLabel(ticket.severity)}
                              </ThemeProvider>
                            </TableCell>
                            <TableCell sx={{ borderBottom: "none" }}>
                              <ThemeProvider theme={theme}>
                                {getStateLabel(ticket.state)}
                              </ThemeProvider>
                            </TableCell>
                            {customerPage && type === 'ticket' && (
                              <TableCell sx={{ borderBottom: "none", color: "#475569", fontWeight: 600 }}>
                                {ticket.service}
                              </TableCell>
                            )}
                            <TableCell sx={{ borderBottom: "none", color: "#475569", fontWeight: 600 }}>
                              {ticket.customer ? capitalizeFirstLetter(`${ticket.customer.firstname} ${ticket.customer.lastname}`) : 'Interne'}
                            </TableCell>
                            <TableCell sx={{ borderBottom: "none", color: "#475569", fontWeight: 600 }}>
                              {ticket.user_in_charge ? `${ticket.user_in_charge.firstname} ${ticket.user_in_charge.lastname}` : 'Aucun'}
                            </TableCell>
                            <TableCell sx={{ borderBottom: "none", color: "#475569", fontWeight: 600 }}>
                              {ticket.normal_delay} jour{ticket.normal_delay >= 2 ? 's' : ''}
                            </TableCell>
                            <TableCell sx={{ borderBottom: "none", color: "#64748b", fontWeight: 700, borderRadius: "0 12px 12px 0" }}>
                              {formatDateDisplay(ticket.created_at)}
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })}
                  {paginatedTickets.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isClientComplaint ? 7 : 9} align="center" sx={{ borderBottom: "none", py: 8 }}>
                        <Typography variant="body1" color="textSecondary">
                          Pas de résultats
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </AnimatePresence>
              </TableBody>
            </Table>
          </Box>
        </Scrollbar>
        <TablePagination
          component="div"
          count={tickets.length}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Lignes par page:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
        />
        {show && (
        <Grid
          item
          md={4}//
          xs={1}
        > 
        {(user.service_manager && user.id_service === infoTicket.service) || (user.service_manager && user.id_service === 0 && infoTicket.service === 1) || infoTicket.creator_id === user.id || (user.id_service === 0 && user.access_level === 0) ? (
        <ShowTicketManager
       
            type={type}
            show={show}
            handleClose={handleClose}
            infoTicket={infoTicket}
            commentList={commentList}
            user={user}
            ticketComment={ticketComment}
            handleTicketCommentChange={handleTicketCommentChange}
            handleKeyUp={handleKeyUp}
            setCommentList={setCommentList}
            setInfoTicket={setInfoTicket}
            setShow={setShow}
            setListServices={setListServices}
            listServices={listServices}
            listCategories={listCategories}
            listMembers={listMembers}
            getCategories={getCategories}
            getServiceMembers={getServiceMembers}
            setTicketService={setTicketService}
            setTicketCategory={setTicketCategory}
            setTicketReferentId={setTicketReferentId}
            setTicketReferentName={setTicketReferentName}
            setTicketSeverity={setTicketSeverity}
            setTicketSolution={setTicketSolution}
            getTicketDocuments={getTicketDocuments}
            handleShow={handleShow}
            ticketService={ticketService}
            ticketSeverity={ticketSeverity}
            ticketDescription={ticketDescription}
            ticketLog={ticketLog}
            ticketFiles={ticketFiles}
            setTicketFiles={setTicketFiles}
            setListMembers={setListMembers}
            majorIncident={majorIncident}
            setOldTicketCorrectiveActions={setOldTicketCorrectiveActions}
            // for modifying
            setNewTicketService={setNewTicketService}
            setNewTicketSeverity={setNewTicketSeverity}
            setNewTicketReferentId={setNewTicketReferentId}
            setNewTicketTracking={setNewTicketTracking}
            setNewTicketSolution={setNewTicketSolution}
            setNewTicketState={setNewTicketState}
            setNewTicketMajorIncident={setNewTicketMajorIncident}
            setNewTicketCategory={setNewTicketCategory}
            setNewTicketDRA={setNewTicketDRA}
            setListCategories={setListCategories}
            setNewTicketAcceptTracking={setNewTicketAcceptTracking}
            setNewTicketDescription={setNewTicketDescription}
            setNewTicketCause={setNewTicketCause}
            setNewTicketDStart={setNewTicketDStart}
            setNewTicketDREff={setNewTicketDREff}
            setNewTicketCorrectiveActions={setNewTicketCorrectiveActions}

            newTicketService={newTicketService}
            newTicketSeverity={newTicketSeverity}
            newTicketReferentId={newTicketReferentId}
            newTicketTracking={newTicketTracking}
            newTicketSolution={newTicketSolution}
            newTicketState={newTicketState}
            newTicketMajorIncident={newTicketMajorIncident}
            newTicketCategory={newTicketCategory}
            newTicketDRA={newTicketDRA}
            newTicketDescription={newTicketDescription}
            newTicketAcceptTracking={newTicketAcceptTracking}
            newTicketCause={newTicketCause}
            newTicketDStart={newTicketDStart}
            newTicketDREff={newTicketDREff}
            newTicketCorrectiveActions={newTicketCorrectiveActions}
         
          // old values /////////////////
            oldTicketService={oldTicketService}
            oldTicketSeverity={oldTicketSeverity}
            oldTicketReferentId={oldTicketReferentId}
            oldTicketTracking={oldTicketTracking}
            oldTicketSolution={oldTicketSolution}
            oldTicketState={oldTicketState}
            oldTicketMajorIncident={oldTicketMajorIncident}
            oldTicketCategory={oldTicketCategory}
            oldTicketDRA={oldTicketDRA}
            oldTicketDescription={oldTicketDescription}
            oldTicketAcceptTracking={oldTicketAcceptTracking} 
            oldTicketCause={oldTicketCause}
            oldTicketDStart={oldTicketDStart}
            oldTicketDREff={oldTicketDREff}
            oldTicketCorrectiveActions={oldTicketCorrectiveActions}

          //
          updateTicketInfo={updateTicketInfo}
          validateTicket={validateTicket}
          update={update}
          setUpdate={setUpdate}
          openTicket={openTicket}
          deleteTicket={deleteTicket}
          getTickets={getTickets}
          deleteTicketDocument={deleteTicketDocument}
          getTicketCorrectiveActions={getTicketCorrectiveActions}
          />
           ) : 
           <ShowTicketReferent
          //  params ticket
            show={show}
            type={type}
            user={user}
            majorIncident={majorIncident}
            ticketComment={ticketComment}
            infoTicket={infoTicket}
            commentList={commentList}
            listServices={listServices}
            listCategories={listCategories}
            listMembers={listMembers}
            ticketService={ticketService}
            ticketSeverity={ticketSeverity}
            ticketDescription={ticketDescription}
            ticketLog={ticketLog}
            ticketFiles={ticketFiles}
            // functions
            handleClose={handleClose}
            handleTicketCommentChange={handleTicketCommentChange}
            handleShow={handleShow}           
            handleKeyUp={handleKeyUp}           
            updateTicketInfo={updateTicketInfo}
            validateTicket={validateTicket}
            update={update}
            setUpdate={setUpdate}
            getTickets={getTickets}    
            getCategories={getCategories}
            getServiceMembers={getServiceMembers}
            getTicketDocuments={getTicketDocuments}
            getTicketCorrectiveActions={getTicketCorrectiveActions}
            // set params
            setCommentList={setCommentList}
            setInfoTicket={setInfoTicket}
            setShow={setShow}
            setListServices={setListServices}
            setTicketService={setTicketService}
            setTicketCategory={setTicketCategory}
            setTicketReferentId={setTicketReferentId}
            setTicketReferentName={setTicketReferentName}
            setTicketSeverity={setTicketSeverity}
            setTicketSolution={setTicketSolution}
            setTicketFiles={setTicketFiles}
            setOldTicketCorrectiveActions={setOldTicketCorrectiveActions}
            // set new values
            setNewTicketAcceptTracking={setNewTicketAcceptTracking}
            setNewTicketService={setNewTicketService}
            setNewTicketSeverity={setNewTicketSeverity}
            setNewTicketReferentId={setNewTicketReferentId}
            setNewTicketTracking={setNewTicketTracking}
            setNewTicketSolution={setNewTicketSolution}
            setNewTicketState={setNewTicketState}
            setNewTicketMajorIncident={setNewTicketMajorIncident}
            setNewTicketCategory={setNewTicketCategory}
            setNewTicketCause={setNewTicketCause}
            setNewTicketDRA={setNewTicketDRA}
            setNewTicketDStart={setNewTicketDStart}
            setNewTicketDREff={setNewTicketDREff}
            setNewTicketCorrectiveActions={setNewTicketCorrectiveActions}
            //  new values
            newTicketService={newTicketService}
            newTicketSeverity={newTicketSeverity}
            newTicketReferentId={newTicketReferentId}
            newTicketTracking={newTicketTracking}
            newTicketSolution={newTicketSolution}
            newTicketState={newTicketState}
            newTicketMajorIncident={newTicketMajorIncident}
            newTicketCategory={newTicketCategory}
            newTicketDRA={newTicketDRA}
            newTicketAcceptTracking={newTicketAcceptTracking}
            newTicketCause={newTicketCause}
            newTicketDStart={newTicketDStart}
            newTicketDREff={newTicketDREff}
            newTicketCorrectiveActions={newTicketCorrectiveActions}
            // old values
              oldTicketService={oldTicketService}
              oldTicketSeverity={oldTicketSeverity}
              oldTicketReferentId={oldTicketReferentId}
              oldTicketTracking={oldTicketTracking}
              oldTicketSolution={oldTicketSolution}
              oldTicketState={oldTicketState}
              oldTicketMajorIncident={oldTicketMajorIncident}
              oldTicketCategory={oldTicketCategory}
              oldTicketDRA={oldTicketDRA}
              oldTicketAcceptTracking={oldTicketAcceptTracking}
              oldTicketCause={oldTicketCause}
              oldTicketDStart={oldTicketDStart}
              oldTicketDREff={oldTicketDREff}
              oldTicketCorrectiveActions={oldTicketCorrectiveActions}
          //

          />
           }


        </Grid>
      )}

      {/* Modal détail (lecture seule) d'une réclamation client, alimenté par la ligne cliquée */}
      <Dialog open={clientDetailOpen} onClose={() => setClientDetailOpen(false)} fullWidth maxWidth="sm">
        {clientDetail && (
          <>
            <DialogTitle sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              Réclamation {clientDetail.reference}
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  {renderDetailField('Apprenant', clientDetail.customer ? capitalizeFirstLetter(`${clientDetail.customer.firstname} ${clientDetail.customer.lastname}`) : '—')}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {renderDetailField('Affectation', clientDetail.user_in_charge ? `${clientDetail.user_in_charge.firstname} ${clientDetail.user_in_charge.lastname}` : 'Non affecté')}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {renderDetailField('Formation', clientDetail.formation)}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {renderDetailField('Session', formatSession(clientDetail.session))}
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              {renderDetailField('Réclamation', clientDetail.description)}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setClientDetailOpen(false)} color="primary">Fermer</Button>
              <Button
                onClick={() => { setClientDetailOpen(false); setClientEditOpen(true); }}
                color="primary"
                variant="contained"
              >
                Modifier
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Formulaire d'édition d'une réclamation client (pré-rempli depuis la ligne) */}
      <ClientComplaintForm
        open={clientEditOpen}
        onClose={() => setClientEditOpen(false)}
        mode="edit"
        initial={clientDetail}
        user={user}
        onSaved={() => { getTickets(); setUpdate(!update); }}
      />
    </Box>
    </Box>
  );
}

TicketList.propTypes = {
  tickets: PropTypes.array.isRequired,
  customerPage: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  update: PropTypes.bool.isRequired,
  setUpdate: PropTypes.func.isRequired,
  getTickets: PropTypes.func.isRequired,
type: PropTypes.string.isRequired,
  currentTab: PropTypes.string,
};
